/**
 * GH #1962: in-situ self-service instead of "contact your rep".
 *
 * The portal used to answer "I need this changed" with a dead end: a sentence
 * telling the FI operator to email someone, or a button that opened their mail
 * client. That is the platform handing its own job back to the user, and the
 * operator has raised it repeatedly.
 *
 * Every one of those sites now routes to the real Support case form
 * (/admin/support/new -> POST /fi-cases), with the request PRE-SCOPED: which
 * surface, which tenant, which field. The operator lands on a form that is
 * already filled in and files a real, tracked case.
 *
 * This is deliberately the existing Support surface rather than the in-situ
 * popover from sprint-in-situ-reporting: that arc is still `proposed`, and a
 * button wired to an unbuilt widget would be the same dead control in nicer
 * clothing. When the popover lands it can consume the same helper.
 */

/**
 * Build the href for a pre-scoped change request.
 *
 * The subject and description are what the operator would otherwise have had to
 * type into an email -- so we type it for them, and it lands somewhere tracked.
 *
 * `surface` is where they are standing ('Organization profile'), `item` is what
 * they want changed ('Charter number'), `detail` is anything that scopes it (the
 * tenant, the custodian, the flag key). The param shape is inlined rather than
 * named: it is a local UI helper, not an API contract, and a named interface here
 * would be an ad-hoc type.
 */
export function requestChangeHref(req: {
  surface: string;
  item: string;
  detail?: string;
}): string {
  const subject = `Request: ${req.item} (${req.surface})`;
  const description = [
    `Surface: ${req.surface}`,
    `Requested item: ${req.item}`,
    ...(req.detail ? [`Context: ${req.detail}`] : []),
    '',
    'Requested change:',
    '',
  ].join('\n');

  const params = new URLSearchParams({ subject, description });
  return `/admin/support/new?${params.toString()}`;
}
